import * as XLSX from 'xlsx';
import type { BvSite, BvOrganisation, BvContact, BvDma, BvRetailContract } from '../types/billValidationTypes';

// ── Date / number helpers ──────────────────────────────────────────────────────
export function fmtDate(d?: string | null) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-AU');
}

export function fmtNum(n?: number | null, dec = 4) {
  if (n == null) return '';
  return Number(n.toFixed(dec));
}

// ── Site join type ─────────────────────────────────────────────────────────────
export type SiteJoin = {
  nmi?: string;
  sf_site_id?: string;
  addr_city?: string;
  addr_state?: string;
  bv_extn?: Record<string, unknown>;
  client?: { id?: string; name?: string; sf_account_id?: string };
};

export function getSiteJoin(raw: unknown): SiteJoin {
  if (!raw || typeof raw !== 'object') return {};
  return raw as SiteJoin;
}

// ── Row flatteners ─────────────────────────────────────────────────────────────

// Column set matches the master sheet's "New - Clients" tab exactly:
// Client ID, SF Account ID, Name, ABN, Status, BV Status, Sites, NMI's,
// Shield Platinum, Google Drive, Broker ID, Created At
export function flattenClient(o: BvOrganisation): Record<string, unknown> {
  const bv = (o.extn?.bv ?? {}) as Record<string, unknown>;
  return {
    'Client ID':       o.id,
    'SF Account ID':   o.sf_account_id ?? '',
    Name:              o.name,
    ABN:               o.abn ?? '',
    Status:            o.status,
    'BV Status':       o.bill_val,
    Sites:             o.site_count ?? '',
    "NMI's":           (o.nmis ?? []).join(', '),
    'Shield Platinum': (o.shield_platinum ?? bv.shield_platinum) ? 'Yes' : 'No',
    'Google Drive':    o.drive_link ?? bv.google_drive_link ?? '',
    'Broker ID':       o.broker ?? bv.broker_id ?? '',
    'Created At':      fmtDate(o.created_at),
  };
}

// Contact fields appended to each site row (contacts no longer export as
// their own sheet — folded into Sites instead, joined via contact.site_id).
// One row per site: the primary contact (is_primary) if set, else the
// earliest by created_at; "All Contacts" lists everyone at that site.
type SiteContact = {
  first_name?: string | null; last_name?: string | null; email?: string | null;
  phone?: string | null; mobile?: string | null; role?: string | null;
  contact_type?: string | null; is_primary?: boolean | null;
  bv_extn?: { mobile?: string; contact_type?: string; is_primary?: boolean } | null;
  created_at?: string;
};

function pickPrimaryContact(contacts: SiteContact[]): SiteContact | undefined {
  if (contacts.length === 0) return undefined;
  const primary = contacts.find((c) => c.is_primary ?? c.bv_extn?.is_primary);
  if (primary) return primary;
  return [...contacts].sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''))[0];
}

// Column set + order matches "New - Sites" tab, plus contact columns
// (First Name, Last Name, Email, Phone, Mobile, Role, Contact Type,
// Is Primary, All Contacts) appended at the end:
// SF Account ID, SF Site ID, NMI, Client, Street, City, State, Postcode,
// Distributor, Tariff Code, Market Region, Market Status, BV Status,
// Enrolment Date, Unenrolment Date, Active Retailer, Meter Provider,
// Retail TOU Schedule, TNI, DLF Code, DLF Value, MLF Code, MLF Value,
// Is Embedded, Created At, [contact columns], All Contacts
export function flattenSite(s: BvSite & { _contacts?: SiteContact[] }): Record<string, unknown> {
  const contacts = s._contacts ?? [];
  const primary = pickPrimaryContact(contacts);
  const allContacts = contacts
    .map((c) => {
      const name = [c.first_name, c.last_name].filter(Boolean).join(' ');
      return c.email ? `${name} <${c.email}>` : name;
    })
    .filter(Boolean)
    .join('; ');

  return {
    'SF Account ID':      s.client?.sf_account_id ?? '',
    'SF Site ID':         s.sf_site_id ?? '',
    NMI:                  s.nmi ?? '',
    Client:               s.client?.name ?? '',
    Street:               s.addr_street ?? '',
    City:                 s.addr_city ?? '',
    State:                s.bv_extn?.state ?? s.addr_state ?? '',
    Postcode:             s.addr_postcode ?? '',
    Distributor:          s.bv_extn?.network_distributor ?? '',
    'Tariff Code':        s.bv_extn?.network_tariff_code ?? '',
    'Market Region':      s.market_region ?? s.bv_extn?.market_region ?? '',
    'Market Status':      s.market_status ?? s.bv_extn?.market_status ?? '',
    'BV Status':          s.bv_extn?.bill_val_status ?? '',
    'Enrolment Date':     s.bv_extn?.enrolment_date ? fmtDate(String(s.bv_extn.enrolment_date)) : '',
    'Unenrolment Date':   s.bv_extn?.unenrolment_date ? fmtDate(String(s.bv_extn.unenrolment_date)) : '',
    'Active Retailer':    s.active_retailer ?? '',
    'Meter Provider':     s.bv_extn?.metering_provider ?? '',
    'Retail TOU Schedule': s.bv_extn?.retail_tou_schedule ?? '',
    TNI:                  s.bv_extn?.tni ?? '',
    'DLF Code':           s.bv_extn?.dlf_code ?? '',
    'DLF Override':       s.bv_extn?.dlf_override ?? '',
    'MLF Code':           s.bv_extn?.mlf_code ?? '',
    'MLF Override':       s.bv_extn?.mlf_override ?? '',
    'Is Embedded':        (s.is_embedded ?? s.bv_extn?.is_embedded) ? 'Yes' : 'No',
    'Created At':         fmtDate(s.created_at),
    'Contact First Name': primary?.first_name ?? '',
    'Contact Last Name':  primary?.last_name ?? '',
    'Contact Email':      primary?.email ?? '',
    'Contact Phone':      primary?.phone ?? '',
    'Contact Mobile':     primary?.mobile ?? primary?.bv_extn?.mobile ?? '',
    'Contact Role':       primary?.role ?? '',
    'Contact Type':       primary?.contact_type ?? primary?.bv_extn?.contact_type ?? '',
    'Contact Is Primary': (primary?.is_primary ?? primary?.bv_extn?.is_primary) ? 'Yes' : 'No',
    'All Contacts':       allContacts,
  };
}

// Column set + order matches "New - Contacts" tab exactly:
// SF Account ID, Site ID, N.M.I, Client, First Name, Last Name, Email,
// Phone, Mobile, Role, Contact Type, Is Primary, Created At
export function flattenContact(
  c: BvContact & {
    client_name?: string; client_sf_account_id?: string;
    site_nmi?: string; site_sf_site_id?: string;
  },
): Record<string, unknown> {
  return {
    'SF Account ID': c.client_sf_account_id ?? '',
    'Site ID':       c.site_sf_site_id ?? '',
    'N.M.I':         c.site_nmi ?? '',
    Client:          c.client_name ?? '',
    'First Name':    c.first_name,
    'Last Name':     c.last_name,
    Email:           c.email ?? '',
    Phone:           c.phone ?? '',
    Mobile:          c.mobile ?? c.bv_extn?.mobile ?? '',
    Role:            c.role ?? '',
    'Contact Type':  c.contact_type ?? c.bv_extn?.contact_type ?? '',
    'Is Primary':    (c.is_primary ?? c.bv_extn?.is_primary) ? 'Yes' : 'No',
    'Created At':    fmtDate(c.created_at),
  };
}

// Column set + order matches "New - DMA Contracts" tab exactly:
// SF Account ID, SF Site ID, NMI, Client, State, DMA Provider,
// Start Date, End Date, SF Site ID, Created At
// The source sheet repeats "SF Site ID" as its own column right before
// Created At (a data-entry quirk in the sheet itself). A JS object can't
// have two identical keys, so the second occurrence uses a trailing space
// ('SF Site ID ') — renders visually identical to "SF Site ID" in Excel/CSV
// since trailing whitespace in a header cell is invisible, but is a
// distinct key so both columns exist with the same value, matching the sheet.
export function flattenDma(d: BvDma & { _site?: unknown }): Record<string, unknown> {
  const s = getSiteJoin(d._site);
  const sfSiteId = s.sf_site_id ?? '';
  return {
    'SF Account ID': s.client?.sf_account_id ?? '',
    'SF Site ID':    sfSiteId,
    NMI:             s.nmi ?? '',
    Client:          s.client?.name ?? '',
    State:           (s.bv_extn?.['state'] as string | undefined) ?? s.addr_state ?? '',
    'DMA Provider':  d.dma_provider ?? '',
    'Start Date':    fmtDate(d.start_date),
    'End Date':      fmtDate(d.end_date),
    'SF Site ID ':   sfSiteId,
    'Created At':    fmtDate(d.created_at),
  };
}

// Column set + order matches "New - Contracts" tab exactly:
// SF Account ID, SF Site ID, NMI, Client, State, Retailer, Contract Start,
// Contract End, Rate Type, Is Current, Source, Notes, Created At
export function flattenRetailContract(
  c: BvRetailContract & { _site?: unknown },
): Record<string, unknown> {
  const s = getSiteJoin(c._site);
  return {
    'SF Account ID':  s.client?.sf_account_id ?? '',
    'SF Site ID':     s.sf_site_id ?? '',
    NMI:              s.nmi ?? '',
    Client:           s.client?.name ?? '',
    State:            (s.bv_extn?.['state'] as string | undefined) ?? s.addr_state ?? '',
    Retailer:         c.retailer?.name ?? '',
    'Contract Start': fmtDate(c.contract_start),
    'Contract End':   fmtDate(c.contract_end),
    'Rate Type':      c.rate_type ?? '',
    'Is Current':     c.is_current ? 'Yes' : 'No',
    Source:           c.source ?? '',
    Notes:            c.notes ?? '',
    'Created At':     fmtDate(c.created_at),
  };
}

// Column set + order matches "New - BV Contract Rates" tab exactly, including
// its unit-variant split (Supply/Metering/Supplementary Services each spread
// across separate day/month/year columns instead of one column + a unit
// field) and its own header typos/line-breaks, reproduced verbatim so the
// download is byte-for-byte the same shape as the source sheet:
//   'Metering ($/meter/day' has no closing paren in the source header.
//   'Supplementary Metering' / 'Supplementary Services' headers contain a
//   literal newline before the unit in parentheses.
function unitColumn(unit: string | undefined, map: Record<string, string>): string | null {
  return unit ? (map[unit] ?? null) : null;
}

export function flattenRates(
  c: BvRetailContract & { _site?: unknown },
): Record<string, unknown>[] {
  const s = getSiteJoin(c._site);
  const nmi      = s.nmi ?? '';
  const sfSiteId = s.sf_site_id ?? '';
  const client   = s.client?.name ?? '';
  const sfAccId  = s.client?.sf_account_id ?? '';
  const retailer = c.retailer?.name ?? '';

  const unitCols = [
    'Supply ($/servicePoint/day)', 'Supply ($/servicePoint/month)', 'Supply ($/servicePoint/year)',
    'Supplementary Metering\n($/meter/year)',
    'Metering ($/meter/year)', 'Metering ($/meter/day', 'Metering ($/servicePoint/year)',
    'Supplementary Services\n($/meter/year)', 'Supplementary Services ($/meter/day)', 'Supplementary Services ($/servicePoint/year)',
  ];

  const baseRow: Record<string, unknown> = {
    'SF Account ID': sfAccId, 'SF Site ID': sfSiteId, NMI: nmi, Client: client, Retailer: retailer,
    'Contract Start': fmtDate(c.contract_start), 'Contract End': fmtDate(c.contract_end),
    'Period Start': '', 'Period End': '',
    'Peak (¢/kWh)': '', 'Shoulder (¢/kWh)': '', 'Off-Peak (¢/kWh)': '',
    'Anytime (¢/kWh)': '', 'Super-Peak (¢/kWh)': '',
    ...Object.fromEntries(unitCols.map((k) => [k, ''])),
    'GreenPower (¢/kWh)': '',
    'Feed-in Anytime (¢/kWh)': '', 'Feed-in Peak (¢/kWh)': '',
    'Feed-in Off-Peak (¢/kWh)': '', 'Feed-in Shoulder (¢/kWh)': '',
  };

  if (!c.rates || c.rates.length === 0) return [baseRow];

  return c.rates.map((r) => {
    const supplyCol = unitColumn(r.supply_unit, {
      per_servicepoint_day: 'Supply ($/servicePoint/day)',
      per_servicepoint_month: 'Supply ($/servicePoint/month)',
      per_servicepoint_year: 'Supply ($/servicePoint/year)',
    });
    const meteringCol = unitColumn(r.metering_unit, {
      per_meter_year: 'Metering ($/meter/year)',
      per_meter_day: 'Metering ($/meter/day',
      per_servicepoint_year: 'Metering ($/servicePoint/year)',
    });
    const suppServicesCol = unitColumn(r.supplementary_services_unit, {
      per_meter_year: 'Supplementary Services\n($/meter/year)',
      per_meter_day: 'Supplementary Services ($/meter/day)',
      per_servicepoint_year: 'Supplementary Services ($/servicePoint/year)',
    });

    return {
      'SF Account ID':                  sfAccId,
      'SF Site ID':                     sfSiteId,
      NMI:                              nmi,
      Client:                           client,
      Retailer:                         retailer,
      'Contract Start':                 fmtDate(c.contract_start),
      'Contract End':                   fmtDate(c.contract_end),
      'Period Start':                   fmtDate(r.period_start),
      'Period End':                     fmtDate(r.period_end),
      'Peak (¢/kWh)':                  fmtNum(r.peak_rate),
      'Shoulder (¢/kWh)':              fmtNum(r.shoulder_rate),
      'Off-Peak (¢/kWh)':              fmtNum(r.off_peak_rate),
      'Anytime (¢/kWh)':               fmtNum(r.anytime_rate),
      'Super-Peak (¢/kWh)':            fmtNum(r.super_peak_rate),
      ...Object.fromEntries(unitCols.map((k) => [k, ''])),
      ...(supplyCol ? { [supplyCol]: fmtNum(r.supply_charge) } : {}),
      ...(meteringCol ? { [meteringCol]: fmtNum(r.metering_rate) } : {}),
      'Supplementary Metering\n($/meter/year)': fmtNum(r.supplementary_metering_rate),
      ...(suppServicesCol ? { [suppServicesCol]: fmtNum(r.supplementary_services_rate) } : {}),
      'GreenPower (¢/kWh)':            fmtNum(r.green_power_rate),
      'Feed-in Anytime (¢/kWh)':       fmtNum(r.feed_in_rate_anytime),
      'Feed-in Peak (¢/kWh)':          fmtNum(r.feed_in_rate_peak),
      'Feed-in Off-Peak (¢/kWh)':      fmtNum(r.feed_in_rate_off_peak),
      'Feed-in Shoulder (¢/kWh)':      fmtNum(r.feed_in_rate_shoulder),
    };
  });
}

// ── Export functions ───────────────────────────────────────────────────────────

export function exportToXLSX(rows: Record<string, unknown>[], filename: string) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Export');
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

function csvCell(val: unknown): string {
  const s = val == null ? '' : String(val);
  return s.includes(',') || s.includes('"') || s.includes('\n')
    ? `"${s.replace(/"/g, '""')}"`
    : s;
}

export function exportToCSV(rows: Record<string, unknown>[], filename: string) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.map(csvCell).join(','),
    ...rows.map((row) => headers.map((h) => csvCell(row[h])).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export type ExportSheet = {
  name: string;
  rows: Record<string, unknown>[];
};

export function exportMultiSheetXLSX(sheets: ExportSheet[], filename: string) {
  const wb = XLSX.utils.book_new();
  for (const sheet of sheets) {
    const ws = sheet.rows.length > 0
      ? XLSX.utils.json_to_sheet(sheet.rows)
      : XLSX.utils.aoa_to_sheet([['No data']]);
    // Truncate sheet name to 31 chars (Excel limit)
    const safeName = sheet.name.slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, safeName);
  }
  XLSX.writeFile(wb, `${filename}.xlsx`);
}
